/**
 * ProfessionalDashboard - Dashboard para profissionais
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Navegação por abas: Buscar Vagas, Oferecer Serviços, Minhas Ofertas
 * - Tooltip dinâmico baseado na aba ativa
 * - Modal de exemplo
 * - Listagem de vagas disponíveis
 * - Listagem de ofertas próprias
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - ExampleModal (componente extraído)
 * - JobsList (componente extraído)
 * - WorkOfferForm (componente extraído)
 * - WorkOfferCard (componente extraído)
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "../translations";
import ExampleModal from "./ExampleModal";
import JobsList from "./JobsList";
import WorkOfferForm from "./WorkOfferForm";
import WorkOfferCard from "./WorkOfferCard";
import { RecommendationsManager } from "./RecommendationsSection";
import { ExternalWebJobsCard } from "./ExternalJobsSection";
import { useAuth } from "../App";

const ProfessionalDashboard = ({ workOffers, myOffers, onUpdate }) => {
  const [activeProfessionalTab, setActiveProfessionalTab] = useState('browse');
  const [showTip, setShowTip] = useState(true);
  const [showExample, setShowExample] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();

  // Get current user ID from token
  const [userId, setUserId] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserId(payload.sub || payload.user_id);
      } catch {}
    }
  }, []);

  // Tooltip dinâmico baseado na aba ativa
  const getTooltipForTab = () => {
    switch (activeProfessionalTab) {
      case 'browse':
        return t('tooltipSearchJobs');
      case 'offer':
        return t('tooltipWorkOffer');
      case 'my-offers':
        return t('tooltipMyOffers');
      default:
        return t('tooltipSearchJobs');
    }
  };

  return (
    <div className="space-y-6">
      {/* Modal de Exemplo */}
      <ExampleModal isOpen={showExample} onClose={() => setShowExample(false)} type="buscarVagas" />

      {/* Convite para a vitrine de Profissionais */}
      {user?.user_type === 'professional' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="vitrine-invite-card">
          <p className="text-sm font-semibold text-amber-900">
            🏅 Quer aparecer na vitrine de Profissionais para os proprietários?
          </p>
          <p className="text-xs text-amber-700 mt-1">
            É lá que produtores buscam gente verificada para contratar. Para subir à vitrine, adicione uma credencial:
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            <a
              href="/certification"
              className="px-3 py-1.5 rounded-lg bg-green-700 text-white text-xs font-semibold hover:bg-green-800"
              data-testid="vitrine-cert-btn"
            >
              🌱 Aderir à certificação (grátis)
            </a>
            <button
              onClick={() => setActiveProfessionalTab('my-offers')}
              className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
              data-testid="vitrine-rec-btn"
            >
              ⭐ Pedir recomendação a um ex-empregador
            </button>
          </div>
        </div>
      )}

      {/* Dica de Como Usar - Dinâmica por Aba */}
      {showTip && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-green-400 hover:text-green-600"
          >
            ✕
          </button>
          <div className="flex items-start gap-3">
            <span className="text-2xl">💡</span>
            <div>
              <h4 className="font-semibold text-green-800">{t('howToUse')}</h4>
              <p className="text-sm text-green-700 mt-1">{getTooltipForTab()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 items-center">
          <button
            onClick={() => setActiveProfessionalTab('browse')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeProfessionalTab === 'browse'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('searchJobsBtn')}
          </button>
          <button
            onClick={() => setActiveProfessionalTab('offer')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeProfessionalTab === 'offer'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('offerServices')}
          </button>
          <button
            onClick={() => setActiveProfessionalTab('my-offers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeProfessionalTab === 'my-offers'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('myOffers')}
          </button>
          
          {/* Botão Ver Exemplo - Abre Modal */}
          <button
            onClick={() => setShowExample(true)}
            className="ml-auto py-2 px-3 text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1"
          >
            💡 {t('seeExample')}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeProfessionalTab === 'browse' && (
        <div data-testid="professional-browse-jobs">
          <JobsList />
          <ExternalWebJobsCard />
        </div>
      )}

      {activeProfessionalTab === 'offer' && (
        <WorkOfferForm onUpdate={onUpdate} />
      )}

      {activeProfessionalTab === 'my-offers' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">{t('myServiceOffers')}</h2>
          <div className="grid gap-4">
            {myOffers.map((offer) => (
              <WorkOfferCard key={offer.id} offer={offer} isOwner={true} onUpdate={onUpdate} />
            ))}
          </div>
          {myOffers.length === 0 && (
            <p className="text-gray-500 text-center py-8">{t('noOffersYet')}</p>
          )}

          {/* Seção de Recomendações e Convites */}
          {userId && (
            <div className="mt-8 border-t pt-6">
              <RecommendationsManager userId={userId} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfessionalDashboard;
